import { ExternalCsmsConfiguration } from '@evimo/core/modules/organization/domain/external-csms-configuration';
import type {
  ExternalCsmsConfigDto,
  ExternalCsmsEndpointDto,
} from '@evimo/types/api/organization/external-csms-config/external-csms-config.dto';
import {
  CsmsEndpointRole,
  MAX_CSMS_SECONDARIES,
} from '@evimo/types/enums/csms-endpoint-role';
import {
  DEFAULT_OCPP_SUBPROTOCOL,
  normalizeOcppSubprotocol,
  type OcppSubprotocol,
} from '@evimo/types/enums/ocpp-subprotocol';

export const WS_URL_REGEX = /^wss?:\/\/[^\s/$.?#].[^\s]*$/i;
export { MAX_CSMS_SECONDARIES };

export type SecondaryCsmsFormEndpoint = {
  id?: string;
  enabled: boolean;
  baseUrl: string;
  subprotocol: OcppSubprotocol;
  sortOrder: number;
};

export type ExternalCsmsFormState = {
  enableCsms: boolean;
  csmsBaseUrl: string;
  csmsSubprotocol: OcppSubprotocol;
  chargerAuthenticationEnabled: boolean;
  /** When omitted, existing Secondary endpoints are preserved. */
  secondaries?: SecondaryCsmsFormEndpoint[];
};

export function getPrimaryEndpoint(
  config: ExternalCsmsConfigDto | null | undefined,
): ExternalCsmsEndpointDto | null {
  return (
    config?.endpoints?.find(
      (endpoint) => endpoint.role === CsmsEndpointRole.PRIMARY,
    ) ?? null
  );
}

function asCsmsConfiguration(
  config: ExternalCsmsConfigDto | null | undefined,
): ExternalCsmsConfiguration | null {
  if (!config) {
    return null;
  }
  return ExternalCsmsConfiguration.create({
    configVersion: config.configVersion ?? 0,
    chargerAuthenticationEnabled: config.chargerAuthenticationEnabled ?? false,
    endpoints: (config.endpoints ?? []).map((endpoint, index) => ({
      id: endpoint.id ?? '',
      role: endpoint.role,
      enabled: endpoint.enabled !== false,
      baseUrl: endpoint.baseUrl ?? '',
      subprotocol: normalizeOcppSubprotocol(endpoint.subprotocol),
      sortOrder: endpoint.sortOrder ?? index,
    })),
  });
}

export function isExternalCsmsProxyEnabled(
  config: ExternalCsmsConfigDto | null | undefined,
): boolean {
  return asCsmsConfiguration(config)?.getUsablePrimary() != null;
}

export function isChargerAuthenticationEnabled(
  config: ExternalCsmsConfigDto | null | undefined,
): boolean {
  return asCsmsConfiguration(config)?.isChargerAuthenticationEnabled() ?? false;
}

export function buildExternalCsmsConfigPayload(
  state: ExternalCsmsFormState,
  existing?: ExternalCsmsConfigDto | null,
): ExternalCsmsConfigDto | null {
  if (!state.enableCsms) {
    return null;
  }

  const baseUrl = state.csmsBaseUrl.trim();
  if (!baseUrl) {
    return null;
  }

  const existingPrimary = getPrimaryEndpoint(existing);
  const secondaries = resolveSecondaryEndpoints(state, existing);

  const primary: ExternalCsmsEndpointDto = {
    ...(existingPrimary?.id ? { id: existingPrimary.id } : {}),
    role: CsmsEndpointRole.PRIMARY,
    enabled: true,
    baseUrl,
    subprotocol: normalizeOcppSubprotocol(state.csmsSubprotocol),
    sortOrder: existingPrimary?.sortOrder ?? 0,
  };

  return {
    chargerAuthenticationEnabled: state.chargerAuthenticationEnabled,
    endpoints: [primary, ...secondaries],
  };
}

export function getSecondaryEndpoints(
  config: ExternalCsmsConfigDto | null | undefined,
): ExternalCsmsEndpointDto[] {
  return (
    config?.endpoints?.filter(
      (endpoint) => endpoint.role === CsmsEndpointRole.SECONDARY,
    ) ?? []
  );
}

export function formStateFromConfig(
  config: ExternalCsmsConfigDto | null | undefined,
): ExternalCsmsFormState {
  const primary = getPrimaryEndpoint(config);
  return {
    enableCsms: isExternalCsmsProxyEnabled(config),
    csmsBaseUrl: primary?.baseUrl ?? '',
    csmsSubprotocol: normalizeOcppSubprotocol(primary?.subprotocol),
    chargerAuthenticationEnabled: isChargerAuthenticationEnabled(config),
    secondaries: getSecondaryEndpoints(config).map((endpoint, index) => ({
      ...(endpoint.id ? { id: endpoint.id } : {}),
      enabled: endpoint.enabled !== false,
      baseUrl: endpoint.baseUrl,
      subprotocol: normalizeOcppSubprotocol(endpoint.subprotocol),
      sortOrder: endpoint.sortOrder ?? index + 1,
    })),
  };
}

export function emptySecondaryEndpoint(
  sortOrder: number,
): SecondaryCsmsFormEndpoint {
  return {
    enabled: true,
    baseUrl: '',
    subprotocol: DEFAULT_OCPP_SUBPROTOCOL,
    sortOrder,
  };
}

export type CsmsSaveConfirmation =
  | 'clear'
  | 'primary'
  | 'removeSecondary'
  | 'disableSecondary';

export function csmsSaveConfirmation(
  state: ExternalCsmsFormState,
  existing?: ExternalCsmsConfigDto | null,
): CsmsSaveConfirmation | null {
  if (isExternalCsmsProxyEnabled(existing) && !state.enableCsms) {
    return 'clear';
  }
  if (primaryIdentityChanged(state, existing)) {
    return 'primary';
  }
  if (state.secondaries == null) {
    return null;
  }
  const saved = getSecondaryEndpoints(existing);
  const currentIds = new Set(
    state.secondaries.flatMap((endpoint) => (endpoint.id ? [endpoint.id] : [])),
  );
  if (saved.some((endpoint) => endpoint.id && !currentIds.has(endpoint.id))) {
    return 'removeSecondary';
  }
  const disabled = state.secondaries.some((endpoint) => {
    if (!endpoint.id || endpoint.enabled) {
      return false;
    }
    return saved.find((item) => item.id === endpoint.id)?.enabled !== false;
  });
  return disabled ? 'disableSecondary' : null;
}

export function primaryIdentityChanged(
  state: ExternalCsmsFormState,
  existing?: ExternalCsmsConfigDto | null,
): boolean {
  const primary = getPrimaryEndpoint(existing);
  if (!primary || !state.enableCsms) {
    return false;
  }
  return (
    primary.baseUrl.trim() !== state.csmsBaseUrl.trim() ||
    normalizeOcppSubprotocol(primary.subprotocol) !==
      normalizeOcppSubprotocol(state.csmsSubprotocol)
  );
}

function resolveSecondaryEndpoints(
  state: ExternalCsmsFormState,
  existing?: ExternalCsmsConfigDto | null,
): ExternalCsmsEndpointDto[] {
  if (!state.secondaries) {
    return getSecondaryEndpoints(existing);
  }

  return state.secondaries
    .slice(0, MAX_CSMS_SECONDARIES)
    .map((endpoint, index) => ({
      ...(endpoint.id ? { id: endpoint.id } : {}),
      role: CsmsEndpointRole.SECONDARY,
      enabled: endpoint.enabled,
      baseUrl: endpoint.baseUrl.trim(),
      subprotocol: normalizeOcppSubprotocol(endpoint.subprotocol),
      sortOrder: endpoint.sortOrder ?? index + 1,
    }));
}

export function validateCsmsBaseUrl(baseUrl: string): boolean {
  const trimmed = baseUrl.trim();
  return trimmed.length > 0 && WS_URL_REGEX.test(trimmed);
}

export function normalizeExternalCsmsConfig(
  config: ExternalCsmsConfigDto | null | undefined,
): ExternalCsmsConfigDto | null | undefined {
  if (!config) {
    return config;
  }

  return {
    ...config,
    endpoints: config.endpoints.map((endpoint) => ({
      ...endpoint,
      subprotocol: normalizeOcppSubprotocol(endpoint.subprotocol),
    })),
  };
}
